import { useCallback, useEffect, useRef, useState } from "react";
import { useAppAuth } from "@/hooks/useAppAuth";
import { useUser } from "@b3dotfun/sdk/global-account/react";
import { markNotificationRead as apiMarkNotificationRead, ensureNotificationSetup, getInAppNotifications, getJWT } from "@/lib/api/notifications";
import { extractEmail } from "@/lib/user/extractEmail";
import { useNotificationStore } from "@/stores/notificationStore";
import { useOrganizations } from "@/hooks/useOrganizations";
const POLL_INTERVAL_MS = 10_000;
export function useNotifications() {
  const {
    user
  } = useUser();
  const {
    isAuthenticated
  } = useAppAuth();
  const {
    selectedOrgId
  } = useOrganizations();
  const notifications = useNotificationStore(state => state.notifications);
  const unreadCount = useNotificationStore(state_0 => state_0.unreadCount);
  const isLoading = useNotificationStore(state_1 => state_1.isLoading);
  const setNotifications = useNotificationStore(state_2 => state_2.setNotifications);
  const setLoading = useNotificationStore(state_3 => state_3.setLoading);
  const markAsReadInStore = useNotificationStore(state_4 => state_4.markAsRead);
  const markAllAsReadInStore = useNotificationStore(state_5 => state_5.markAllAsRead);
  const [setupDone, setSetupDone] = useState(false);
  // Track per-(userId,email) so the setup re-runs after a user switch and
  // also re-runs once if the user object hydrates an email asynchronously.
  // ensureNotificationSetup is idempotent, so re-runs are safe.
  const setupKeyRef = useRef<string | null>(null);
  const initialOrgIdRef = useRef<string | null | undefined>(undefined);
  const fetchNotifications = useCallback(async () => {
    const jwt = getJWT();
    if (!jwt) return;
    try {
      const data = await getInAppNotifications(jwt, selectedOrgId || undefined);
      setNotifications(data);
    } catch {
      // Silently fail — polling will retry
    }
  }, [setNotifications, selectedOrgId]);

  // Latest-ref pattern: keeps polling effect stable (no tear-down on org switch)
  // while still calling the up-to-date fetch with the current selectedOrgId.
  const fetchNotificationsRef = useRef(fetchNotifications);
  useEffect(() => {
    fetchNotificationsRef.current = fetchNotifications;
  }, [fetchNotifications]);
  const markNotificationRead = useCallback(async (notificationId: string) => {
    markAsReadInStore(notificationId);
    const jwt_0 = getJWT();
    if (jwt_0) {
      await apiMarkNotificationRead(jwt_0, notificationId);
    }
  }, [markAsReadInStore]);
  const markAllNotificationsRead = useCallback(async () => {
    const unread = notifications.filter(n => !n.read);
    markAllAsReadInStore();
    const jwt_1 = getJWT();
    if (!jwt_1) return;
    await Promise.allSettled(unread.map(n_0 => apiMarkNotificationRead(jwt_1, n_0.id)));
  }, [notifications, markAllAsReadInStore]);

  // Auto-setup: register user, add in_app channel, create default settings
  useEffect(() => {
    if (!isAuthenticated || !user?.userId) return;
    const email = extractEmail(user);
    const key = `${user.userId}:${email ?? ""}`;
    if (setupKeyRef.current === key) return;
    setupKeyRef.current = key;
    const setup = async () => {
      const jwt_2 = getJWT();
      if (!jwt_2) return;
      try {
        setLoading(true);
        await ensureNotificationSetup(jwt_2, email);
        await fetchNotifications();
        setSetupDone(true);
      } catch {
        // Setup failed — will retry on next render that produces a new key
        setupKeyRef.current = null;
      } finally {
        setLoading(false);
      }
    };
    setup();
  }, [isAuthenticated, user, fetchNotifications, setLoading]);

  // Re-fetch when selected org changes. The setup effect handles the initial
  // fetch, so skip the first firing here to avoid a redundant mount-time call.
  useEffect(() => {
    if (initialOrgIdRef.current === undefined) {
      initialOrgIdRef.current = selectedOrgId;
      return;
    }
    if (!isAuthenticated || !setupDone) return;
    if (selectedOrgId === initialOrgIdRef.current) return;
    initialOrgIdRef.current = selectedOrgId;
    fetchNotifications();
  }, [selectedOrgId, isAuthenticated, setupDone, fetchNotifications]);

  // Poll for new notifications. Uses the ref so the interval is not torn down
  // and recreated every time fetchNotifications changes (e.g. on org switch).
  useEffect(() => {
    if (!isAuthenticated || !setupDone) return;
    const interval = setInterval(() => fetchNotificationsRef.current(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, setupDone]);
  return {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead
  };
}